import { useIsMobile } from '@components/common/IsMobile/useIsMobile'
import { useRouter } from 'next/router'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { BreadcrumbItem } from './BreadcrumbItem'
import type {
    ProductCategory,
    RequiredProductBreadcrumbsProps
} from './Breadcrumbs'
import { IS_DEFAULT_BREADCRUMB_ENABLED } from '@components/StoreConfiguration/StoreConfiguration'
import { Category_Url } from '@components/UrlConfiguration/UrlConfiguration'

export const BreadcrumbsPDP = (
    props: RequiredProductBreadcrumbsProps
): JSX.Element => {
    const isMobile = useIsMobile()
    const { product, rootTextVal, urlSuffix, urlPrefix } = props
    const router = useRouter()
    const [breadcrumb, setBreadcrumb] = useState<ProductCategory>([])

    // logic: take the first set of categories till the most deeply nested one
    const { breadcrumbsData } = product.categories.reduce(
        (acc, category) => {
            if (acc.blockAccumulation) return acc

            const { url_path } = category
            if (!url_path) return acc

            const subCategories = url_path.split('/')

            if (subCategories.length > acc.maxLength) {
                acc.maxLength = subCategories.length
                acc.breadcrumbsData.push(category)
            } else {
                // if category with the same nesting level appears
                // stop accumulating new categories
                acc.blockAccumulation = true
            }
            return acc
        },
        { maxLength: 0, breadcrumbsData: [], blockAccumulation: false } as {
            maxLength: number
            breadcrumbsData: {
                name: string
                url_path: string | null
                breadcrumbs?:
                    | {
                          category_id: null
                          category_name: string
                          category_uid: string
                          category_url_path: string | null
                          __typename: string
                      }[]
                    | undefined
                    | null
            }[]
            blockAccumulation: boolean
        }
    )

    const isPrevCategoryPath = (asPath: string | null): boolean | undefined =>
        asPath?.includes(`${Category_Url}/`)

    const setPrimaryCategory = (): boolean | undefined => {
        const previousPath = sessionStorage.getItem('prevPath')
        const isPrevCategory = isPrevCategoryPath(previousPath)
        const { primary_category: { name, url_path } = {} } = product

        if (name && url_path) {
            return IS_DEFAULT_BREADCRUMB_ENABLED && !isPrevCategory
        } else {
            return false
        }
    }

    useEffect(() => {
        const plpCategoryUrlPaths = localStorage.getItem('plpUrlPath')
        const urlPathKey = router.query?.product_url as string
        const plpCategoryUrlPath =
            plpCategoryUrlPaths && urlPathKey
                ? JSON.parse(plpCategoryUrlPaths)[urlPathKey]
                : null
        const category = product.categories.filter(
            (i) => i.url_path === plpCategoryUrlPath
        )

        if (setPrimaryCategory()) {
            const { primary_category: { name, url_path } = {} } = product
            if (!name || !url_path) return
            const primary_category = [
                {
                    name: name,
                    url_path: url_path
                }
            ]
            setBreadcrumb(primary_category)
            return
        }

        if (category.length && category[0]?.breadcrumbs?.length) {
            const bCrumbs = category[0].breadcrumbs.map((itm) => {
                return {
                    name: itm.category_name,
                    url_path: itm?.category_url_path
                }
            })

            setBreadcrumb([
                ...bCrumbs,
                {
                    name: category[0].name,
                    url_path: category[0].url_path
                }
            ])
            return
        }

        setBreadcrumb(breadcrumbsData)
    }, [product.categories])

    const breadCrumbs = useMemo(() => breadcrumb, [breadcrumb])

    return (
        <span>
            <BreadcrumbItem url="/" title={rootTextVal} />
            {breadCrumbs.map((breadcrumb, index) => {
                const { name = '', url_path } = breadcrumb
                return (
                    <Fragment key={url_path}>
                        {props.separator}
                        <BreadcrumbItem
                            url={`${urlPrefix}/${url_path}${urlSuffix}`}
                            title={name}
                            key={url_path}
                        />
                        {index === breadCrumbs.length - 1 && (
                            <>
                                {props.separator}
                                <BreadcrumbItem title={product.name} active />
                            </>
                        )}
                    </Fragment>
                )
            })}
            {!breadCrumbs.length && (
                <>
                    {props.separator}
                    <BreadcrumbItem title={product.name} active />
                </>
            )}
        </span>
    )
}
